import React, { useEffect, useState } from 'react'
import { useQuery } from 'react-query'
import { Icon } from '../../components/Icon'
import { Accordion, AccordionItem } from '../candidates/components/Accordion'
import UIButton from '../../components/UI/UIButton'
import UIPasswordInput from '../../components/UI/UIPasswordInput'
import {
  addNewPassword,
  getUserSettings,
  updateUserSettings,
} from '../../api/settings.tsx/settings'
import { ErrorResponse, incidentData } from '../../utils/types'
import { toast } from 'react-toastify'
import UISwitch from '../../components/UI/UISwitch'

interface UserSettingsResponse {
  status: string
  data: { reminderEmail: boolean }
}

export interface Password {
  oldPassword: string
  newPassword: string
}

const initialData: Password = {
  oldPassword: '',
  newPassword: '',
}

const Account: React.FC = () => {
  const [passwordData, setPasswordData] = useState<Password>(initialData)
  const [reminderEmail, setReminderEmail] = useState<boolean>(true)

  const userSettings = useQuery('userSettings', async () => {
    const response = (await getUserSettings()) as UserSettingsResponse
    return response?.data
  })

  useEffect(() => {
    if (userSettings.data) setReminderEmail(userSettings.data.reminderEmail)
  }, [userSettings.data])

  const handleReminderEmailChange = async (value: boolean): Promise<void> => {
    setReminderEmail(value)
    try {
      const response = (await updateUserSettings({
        reminderEmail: value,
      })) as { status?: string }
      if (response?.status !== 'successful') throw new Error()
      toast.success(
        value ? 'Reminder emails turned on.' : 'Reminder emails turned off.',
        { position: 'top-right' }
      )
    } catch (error) {
      // Put the switch back so it never shows a setting that wasn't saved
      setReminderEmail(!value)
      toast.error('Could not update reminder emails. Please try again.', {
        position: 'top-right',
      })
    }
  }

  const validatePassword = (password: string): boolean => {
    const minLength = 8
    const hasSpecialSymbol = /[!@#$%^&*(),.?":{}|<>]/.test(password)
    const hasUpperCase = /[A-Z]/.test(password)
    return password.length >= minLength && hasSpecialSymbol && hasUpperCase
  }

  const createNewPassword = async (): Promise<void> => {
    if (!validatePassword(passwordData.newPassword)) {
      toast.error(
        'Password must be at least 8 characters long and contain at least one special symbol and one uppercase letter.',
        { position: 'top-right' }
      )
      return
    }

    try {
      const response = (await addNewPassword(passwordData)) as {
        status?: string
        data: incidentData
      }
      if (response?.status === 'successful') {
        toast.success('Password Updated.', { position: 'top-right' })
      } else {
        toast.error('Password not matched', { position: 'top-right' })
      }
    } catch (error) {
      const errors = error as ErrorResponse
      const errorMessage = errors.response?.data?.message || 'An error occurred'
      toast.error(errorMessage, { position: 'top-right' })
    }
  }

  const handleChange = (fieldName: keyof Password, value: string): void => {
    setPasswordData((prevState) => ({
      ...prevState,
      [fieldName]: value,
    }))
  }

  return (
    <>
      <div className="flex my-8 gap-2">
        <div className="flex flex-col w-8/12 gap-4">
          <div className="w-full p-4 bg-gray3 rounded-xl flex gap-2">
            <Icon
              name={'IconInfoCircle'}
              width={16}
              height={16}
              stroke="#02698C"
              strokeWidth={1.2}
            />
            <div className="flex-1">
              <p className="text-sm font-semibold leading-6 text-text2">
                Your Profile
              </p>
              <p className="text-sm font-normal leading-6 text-bluishGreen">
                Your Account email and information is managed in your SCR Record
              </p>
            </div>
            {/* <Icon
              name={'IconChevronRight'}
              width={10}
              height={10}
              stroke="#02698C"
              strokeWidth={1.2}
            /> */}
          </div>
          <Accordion defaultActiveIndex={0}>
            <AccordionItem
              onClick={() => console.log('Accordion')}
              title={'Reminder Emails'}
              isActive={false}
            >
              <div className="flex flex-col gap-4 mt-2 my-4 px-1">
                <div className="w-full p-4 bg-gray1 rounded-xl flex">
                  <div className="flex-1">
                    <p className="text-sm font-semibold leading-6 text-text4">
                      Reminder Email Enabled
                    </p>
                    <p className="text-sm font-normal leading-6 text-text3">
                      Email me when staff training or safeguarding records are
                      near their expiry date, or a policy is due for review.
                      Incident alerts and whistleblowing reports are always
                      sent.
                    </p>
                  </div>
                  <div>
                    <UISwitch
                      value={reminderEmail}
                      onValueChange={handleReminderEmailChange}
                    />
                  </div>
                </div>
              </div>
            </AccordionItem>
          </Accordion>
          <Accordion defaultActiveIndex={0}>
            <AccordionItem
              onClick={() => console.log('Accordion')}
              title={'Password Reset'}
              isActive={false}
            >
              <div className="flex flex-col gap-4 mt-2 my-4 px-1">
                <div className="flex flex-col w-full gap-5">
                  <UIPasswordInput
                    onChange={(e) =>
                      handleChange('oldPassword', e.target.value)
                    }
                    value={passwordData.oldPassword}
                    placeholder="Your old password"
                    label={'Old Password'}
                    iconName={'IconLock'}
                  />
                  <UIPasswordInput
                    onChange={(e) =>
                      handleChange('newPassword', e.target.value)
                    }
                    value={passwordData.newPassword}
                    placeholder="Your new password"
                    label={'New Password'}
                    iconName={'IconLock'}
                    labelBottomList={[
                      `Length: Make your password at least 8 - 12 characters long.`,
                      `Mix It Up: Use a combination of uppercase and lowercase letters, numbers, and special characters.`,
                      `Avoid Common Patterns: Stay away from easy-to-guess sequences like "123456" or "password"`,
                      `No Personal Info: Don't use personal information like names or birthdays in your password. Keep your password strong and secure to protect your personal data!`,
                    ]}
                  />
                  <div className="w-[30%]">
                    <UIButton
                      label="Change"
                      type="button"
                      onClick={createNewPassword}
                      disabled={passwordData.newPassword === ''}
                    />
                  </div>
                </div>
              </div>
            </AccordionItem>
          </Accordion>
        </div>
      </div>
      <div className="bg-gray3 w-full h-[1px] "></div>
      <div className="w-[33%] mt-6 gap-[20px] flex">
        <UIButton label="Update" />
      </div>
    </>
  )
}

export default Account
